import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, MapPin, List, Map, Heart, Info, SlidersHorizontal, ArrowUpDown, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import ExploreMap, { type ExploreMapHandle, type MapBounds } from "./ExploreMap";
import { MobileExploreCard } from "./MobileExploreCard";
import { ExplorePreviewSheet } from "./ExplorePreviewSheet";
import { ExploreFooterNav } from "./ExploreFooterNav";
import { ExploreFilterSheet } from "./ExploreFilterSheet";
import { ExploreSortSheet } from "./ExploreSortSheet";
import { ExploreActiveFilters } from "./ExploreActiveFilters";
import { ExploreLocationPrompt } from "./ExploreLocationPrompt";
import { ExploreEmptyState } from "./ExploreEmptyState";
import { ExploreSectionList } from "./ExploreSectionList";
import { UtflyktSection } from "./UtflyktSection";
import { SearchInAreaButton, useSearchInArea } from "./SearchInAreaButton";
import ExploreRail from "./ExploreRail";
import type { ExploreMode } from "./ExploreModeToggle";
import { useExploreResults, itemKey } from "@/hooks/useExploreResults";
import { useExploreLocation } from "@/hooks/useExploreLocation";
import { useExploreSections } from "@/hooks/useExploreSections";
import { getExploreHistory } from "@/lib/exploreHistory";
import { useExploreCardClick, useThrottledMapTrack } from "@/hooks/useExploreCardClick";
import { useExploreHeroTitle } from "@/hooks/useExploreHeroTitle";
import { ExploreHero } from "./ExploreHero";
import { ExplorePaths, deriveActivePathKey } from "./ExplorePaths";
import { DEFAULT_RADIUS, NEAR_ME_ZOOM, type RadiusKm } from "@/lib/map/config";

import type { ExploreResultItem } from "@/types/exploreResult";
import SiteHeader from "@/components/SiteHeader";
import { useFavorites } from "@/components/favorites/FavoritesProvider";
import { Skeleton } from "@/components/ui/skeleton";
import {
  parseExploreParams,
  buildExploreParamUpdates,
  defaultSortForMode,
  type ExploreParams,
  SORT_LABELS,
} from "@/lib/exploreParams";

type MobileView = "map" | "list";

function parseModeFromUrl(params: URLSearchParams): ExploreMode {
  const m = params.get("mode");
  if (m === "do" || m === "farms") return m;
  return "places"; // default mode is "places"
}

function bucketCount(n: number): string {
  if (n <= 0) return "0";
  if (n <= 1) return "1";
  if (n <= 3) return "2–3";
  if (n <= 5) return "4–5";
  if (n <= 10) return "6–10";
  if (n <= 25) return "11–25";
  if (n <= 50) return "26–50";
  if (n <= 100) return "51–100";
  return "101+";
}

function bucketRadiusKm(r: number): string {
  if (r <= 1) return "≤1km";
  if (r <= 3) return "1–3km";
  if (r <= 5) return "3–5km";
  if (r <= 10) return "5–10km";
  if (r <= 25) return "10–25km";
  if (r <= 50) return "25–50km";
  return "50km+";
}

function bucketPosition(idx: number): "1" | "2–3" | "4–10" | "11+" {
  if (idx <= 0) return "1";
  if (idx <= 2) return "2–3";
  if (idx <= 9) return "4–10";
  return "11+";
}

function bucketDistanceKm(d?: number): string {
  if (d == null || !Number.isFinite(d)) return "unknown";
  if (d < 1) return "<1km";
  if (d < 3) return "1–3km";
  if (d < 5) return "3–5km";
  if (d < 10) return "5–10km";
  if (d < 25) return "10–25km";
  if (d < 50) return "25–50km";
  return "50km+";
}


function bucketBool(b: boolean): "yes" | "no" {
  return b ? "yes" : "no";
}

import { EXPLORE_DEBUG } from "@/lib/debug";
import { track } from "@/lib/analytics";

const ExploreMobile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mapRef = useRef<ExploreMapHandle>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { isFavorited, listFavorites } = useFavorites();

  // Mode & filter params from URL
  const mode = parseModeFromUrl(searchParams);
  const favActive = searchParams.get("fav") === "1";
  const exploreParams = useMemo(() => parseExploreParams(searchParams, mode), [searchParams, mode]);

  // Filter/sort sheet state
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  /** Apply partial filter/sort updates to URL */
  const applyParams = useCallback((updates: Partial<ExploreParams>) => {
    const paramUpdates = buildExploreParamUpdates(updates, mode);
    setSearchParams((prev) => {
      Object.entries(paramUpdates).forEach(([key, val]) => {
        if (val === null) prev.delete(key);
        else prev.set(key, val);
      });
      return prev;
    }, { replace: true });
  }, [mode, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("when");
      prev.delete("type");
      prev.delete("sort");
      prev.delete("tags");
      prev.delete("near");
      return prev;
    }, { replace: true });
    exploreLocation.clear();
  }, [setSearchParams]);

  // Guard: ensure body scroll is never left locked when ExploreMobile mounts/unmounts.
  // Radix Sheet can leave overflow:hidden or pointer-events:none on body after rapid open/close.
  useEffect(() => {
    // Restore on mount in case a previous overlay left stale styles
    document.body.style.overflow = "";
    document.body.style.pointerEvents = "";
    return () => {
      document.body.style.overflow = "";
      document.body.style.pointerEvents = "";
    };
  }, []);

  // Debug logging (only when VITE_EXPLORE_DEBUG=true or in dev)
  useEffect(() => {
    if (!EXPLORE_DEBUG) return;
    const host = import.meta.env.VITE_SUPABASE_URL?.replace(/https?:\/\//, "").split(".")[0] ?? "?";
    console.log(`[Explore] route=${location.pathname} mode=${mode} supaHost=${host}`);
  }, [mode]);

  const setMode = useCallback((m: ExploreMode) => {
    if (EXPLORE_DEBUG) console.log("[Explore] setMode ->", m); // eslint-disable-line no-console
    setSearchParams((prev) => {
      if (m === "places") {
        prev.delete("mode"); // "places" is default, no param needed
      } else {
        prev.set("mode", m);
      }
      return prev;
    }, { replace: true });
    listRef.current?.scrollTo({ top: 0 });
  }, [setSearchParams]);

  const toggleFav = useCallback(() => {
    setSearchParams((prev) => {
      if (prev.get("fav") === "1") {
        prev.delete("fav");
      } else {
        prev.set("fav", "1");
      }
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  // View
  const [viewMode, setViewMode] = useState<MobileView>("list");
  const [radiusKm] = useState<RadiusKm>(DEFAULT_RADIUS);

  // ── Explore location (three-state: none | geo | city) ──
  const exploreLocation = useExploreLocation();
  const nearMeActive = exploreLocation.isActive;
  const userLocation = exploreLocation.userLocation;

  // Sync URL near param with location state
  useEffect(() => {
    const urlNear = searchParams.get("near") === "1";
    if (exploreLocation.isActive && !urlNear) {
      setSearchParams((prev) => {
        prev.set("near", "1");
        prev.set("sort", "nearest");
        return prev;
      }, { replace: true });
    } else if (!exploreLocation.isActive && urlNear) {
      setSearchParams((prev) => {
        prev.delete("near");
        if (prev.get("sort") === "nearest") prev.delete("sort");
        return prev;
      }, { replace: true });
    }
  }, [exploreLocation.isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection via stable key (kind:id) ──
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // UI state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");

  // "Search in this area" state
  const searchInArea = useSearchInArea();
  const [areaBounds, setAreaBounds] = useState<MapBounds | null>(null);

  // Favorite IDs for current mode
  const favIds = useMemo(() => {
    const kind = mode === "farms" ? "producer" : mode === "do" ? "activity" : "place";
    return new Set(listFavorites(kind).map(f => f.entityId));
  }, [listFavorites, mode]);

  // ── Single Source of Truth ──
  const { results, mapItems, isLoading, isFallback, debugCounts } = useExploreResults({
    mode,
    placeFilters: { pageSize: 200, sort: "standard" },
    nearMeActive,
    userLocation,
    radiusKm,
    favActive,
    favIds,
    searchQuery: activeSearchQuery || undefined,
    exploreParams,
  });

  const showDebugOverlay = import.meta.env.VITE_EXPLORE_DEBUG === "true";

  // ── Preserve selection across re-renders ──
  useEffect(() => {
    if (selectedKey && results.length > 0) {
      const stillExists = results.some(r => itemKey(r) === selectedKey);
      if (!stillExists) {
        setSelectedKey(null);
        setPreviewOpen(false);
      }
    }
  }, [results, selectedKey]);

  // Derive selected item from stable key
  const selectedItem = useMemo(
    () => results.find(r => itemKey(r) === selectedKey) ?? null,
    [results, selectedKey]
  );

  // Near me — uses explore location hook
  const handleNearMe = useCallback(async () => {
    track(
      "explore_near_me_click",
      {
        action: nearMeActive ? "disable" : "enable",
        had_location_cached: !!userLocation,
        view_mode: viewMode,
        radius_bucket: bucketRadiusKm(radiusKm),
      },
      "explore"
    );

    if (nearMeActive) {
      exploreLocation.clear();
      return;
    }

    const ok = await exploreLocation.requestGeo();

    track(
      "explore_near_me_result",
      {
        ok,
        error: ok ? undefined : "denied_or_unavailable",
        radius_bucket: bucketRadiusKm(radiusKm),
      },
      "explore"
    );

    if (ok) {
      setViewMode("map");
    }
  }, [nearMeActive, userLocation, viewMode, radiusKm, exploreLocation]);


  // ── Selection handlers using stable key ──
  const openPreview = useCallback((item: ExploreResultItem) => {
    setSelectedKey(itemKey(item));
    setPreviewOpen(true);
  }, []);

  const handleMapItemSelect = useCallback((id: string | null) => {
    if (!id) { setSelectedKey(null); return; }
    // Find the item in results to build stable key
    const item = results.find(r => r.id === id);
    if (!item) return;
    // UX rule: events navigate directly from map in v1 (List = Go. Map = Peek/Go)
    if (item.kind === "activity" && item.href) {
      navigate(item.href);
      return;
    }
    setSelectedKey(itemKey(item));
    setPreviewOpen(true);
    console.log("[Explore] Map item selected:", item); // eslint-disable-line no-console
  }, [results, navigate]);
  // Throttled map move tracker
  const trackMapMove = useThrottledMapTrack(3000);

  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    searchInArea.handleBoundsChange(bounds);
    // Throttled analytics
    const centerLat = (bounds.north + bounds.south) / 2;
    const centerLng = (bounds.east + bounds.west) / 2;
    trackMapMove(centerLat, centerLng);
  }, [searchInArea, trackMapMove]);

  const handleSearchSubmit = useCallback(() => {
    setActiveSearchQuery(searchQuery.trim());
    setSearchOpen(false);
  }, [searchQuery]);

  // Search placeholder per mode
  const searchPlaceholder = mode === "do"
    ? "Sök utflykter och event…"
    : mode === "farms"
      ? "Sök gårdar…"
      : "Sök platser…";

  // Footer nav height compensation
  // Footer nav uses pb-14 on content area


  // Active filter count for badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (favActive) count++;
    if (nearMeActive) count++;
    if (mode === "do" && exploreParams.when !== "upcoming") count++;
    if (mode === "do") {
      const allTypes: string[] = ["events", "outings"];
      const isAll = exploreParams.contentTypes.length === allTypes.length && allTypes.every(t => exploreParams.contentTypes.includes(t as any));
      if (!isAll) count++;
    }
    if (mode === "places" && exploreParams.tags.length > 0) count += exploreParams.tags.length;
    return count;
  }, [favActive, nearMeActive, mode, exploreParams]);

  // Recenter handler
  const handleRecenter = useCallback(() => {
    if (userLocation && mapRef.current) {
      mapRef.current.flyTo(userLocation.lng, userLocation.lat, NEAR_ME_ZOOM);
    }
  }, [userLocation]);

  // ── Explore sections (curated rhythm) ──
  const exploreHistory = useMemo(() => getExploreHistory(), []);
  const sections = useExploreSections(results, {
    hasLocation: nearMeActive && !!userLocation,
    curatedTags: exploreHistory.tags,
    curatedIds: exploreHistory.ids,
    mode,
  });

  // Mode label for empty state
  const modeLabel = mode === "do" ? "aktiviteter" : mode === "places" ? "platser" : "gårdar";

  // Shared card click handler (records history + analytics + navigate)
  const handleExploreCardClick = useExploreCardClick();

  // Dynamic hero title
  const heroTitle = useExploreHeroTitle({
    locationState: exploreLocation.location,
    boundsActive: !!areaBounds,
  });

  // Search in area handler
  const handleSearchInArea = useCallback((bounds: MapBounds) => {
    setAreaBounds(bounds);
    searchInArea.handleSearchInArea(bounds);
  }, [searchInArea]);

  // Filter results by area bounds if active
  const areaFilteredResults = useMemo(() => {
    if (!areaBounds) return results;
    return results.filter((r) => {
      if (r.lat == null || r.lng == null) return false;
      return (
        r.lat >= areaBounds.south &&
        r.lat <= areaBounds.north &&
        r.lng >= areaBounds.west &&
        r.lng <= areaBounds.east
      );
    });
  }, [results, areaBounds]);

  return (
    <div className="flex flex-col h-[100dvh] bg-background">
      {/* Debug badge – only visible with VITE_EXPLORE_DEBUG=true */}
      {EXPLORE_DEBUG && (
        <div className="fixed top-0 right-0 z-[9999] bg-muted text-muted-foreground text-[10px] font-mono px-2 py-0.5 rounded-bl opacity-75 pointer-events-none">
          DEBUG | {mode}
        </div>
      )}
      <SiteHeader />

      {/* Sticky toolbar */}
      <div className="shrink-0 bg-background border-b border-border z-20">
        {/* Row 1: Search + view toggle */}
        <div className="flex items-center gap-2 px-4 pt-3 pb-2">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex-1 flex items-center gap-2 h-11 px-3 rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground"
            aria-label="Sök"
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="truncate">{activeSearchQuery || searchPlaceholder}</span>
          </button>

          <Button
            variant="outline"
            size="icon"
            className="h-11 w-11 shrink-0"
            onClick={() => setViewMode(viewMode === "map" ? "list" : "map")}
            aria-label={viewMode === "map" ? "Visa lista" : "Visa karta"}
          >
            {viewMode === "map" ? <List className="h-5 w-5" /> : <Map className="h-5 w-5" />}
          </Button>
        </div>

        {/* Row 2: Action chips */}
        <div className="flex items-center gap-2 px-4 pb-2 overflow-x-auto scrollbar-none" style={{ WebkitOverflowScrolling: "touch" }}>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 h-8 gap-1.5 text-xs rounded-full relative"
            onClick={() => setFilterOpen(true)}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Filter
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="shrink-0 h-8 gap-1.5 text-xs rounded-full"
            onClick={() => setSortOpen(true)}
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            {SORT_LABELS[exploreParams.sort]}
          </Button>

          <div className="w-px h-4 bg-border shrink-0" />

          <Button
            variant={nearMeActive ? "default" : "outline"}
            size="sm"
            className="shrink-0 h-8 gap-1.5 text-xs rounded-full"
            onClick={handleNearMe}
            disabled={exploreLocation.loading}
          >
            <MapPin className="h-3.5 w-3.5" />
            {exploreLocation.loading ? "Söker…" : nearMeActive ? (exploreLocation.label ?? "Nära mig") + " ✓" : "Nära mig"}
          </Button>
        </div>

        {/* Row 3: Active filter chips */}
        <ExploreActiveFilters
          mode={mode}
          params={exploreParams}
          nearMeActive={nearMeActive}
          favActive={favActive}
          onRemove={applyParams}
          onRemoveNearMe={handleNearMe}
          onRemoveFav={toggleFav}
          onClearAll={clearAllFilters}
        />
      </div>

      {/* Location prompt removed — "Nära mig" lives in chip bar */}

      {/* DEL 1: Debug overlay — visible in dev or when VITE_EXPLORE_DEBUG=true */}
      {showDebugOverlay && (
        <div className="shrink-0 bg-black/80 text-[10px] text-green-400 font-mono px-3 py-1 flex gap-3 z-30">
          <span>raw:{debugCounts.raw}</span>
          <span>norm:{debugCounts.normalized}</span>
          <span>filt:{debugCounts.filtered}</span>
          <span>map:{debugCounts.mappable}</span>
          <span>sel:{selectedKey ?? "–"}</span>
          {isFallback && <span className="text-yellow-400">FALLBACK</span>}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 min-h-0 relative overflow-hidden mb-[calc(56px+env(safe-area-inset-bottom,0px))]" data-testid="explore-map-region">
        {/* MAP VIEW */}
        {viewMode === "map" && (
          <>
            <div className="absolute inset-0" data-testid="explore-map-container">
              <ExploreMap
                ref={mapRef}
                items={mapItems}
                userLocation={userLocation}
                selectedItemId={selectedItem?.id ?? null}
                previewOpen={previewOpen}
                onItemSelect={handleMapItemSelect}
                onBoundsChange={handleBoundsChange}
              />
            </div>
            {/* "Sök i detta område" button — 12/10 signature */}
            <SearchInAreaButton
              bounds={searchInArea.activeBounds}
              onSearchInArea={handleSearchInArea}
              visible={searchInArea.showButton}
            />
            {/* Floating "Centrera på mig" button */}
            {userLocation && (
              <button
                onClick={handleRecenter}
                className="absolute top-3 right-3 z-30 h-10 w-10 rounded-full bg-background shadow-md border border-border flex items-center justify-center hover:bg-muted transition-colors"
                aria-label="Centrera på min position"
                style={{ marginTop: "88px" }} // below MapLibre zoom controls
              >
                <Crosshair className="h-5 w-5 text-primary" />
              </button>
            )}
            {/* Floating rail over map */}
            <div
              className="absolute left-0 right-0 z-20 pointer-events-none"
              style={{ bottom: "calc(60px + env(safe-area-inset-bottom, 0px))" }}
            >
              <div className="pointer-events-auto px-3">
                <ExploreRail
                  results={results}
                  selectedKey={selectedKey}
                  onSelectKey={(key) => {
                    setSelectedKey(key);

                    const item = results.find((r) => itemKey(r) === key);
                    if (!item) return;

                    const idx = results.findIndex((r) => itemKey(r) === key);

                    track(
                      "explore_rail_select",
                      {
                        kind: item.kind,
                        position_bucket: bucketPosition(idx),
                        distance_km_bucket: bucketDistanceKm((item as any)._distanceKm),
                      },
                      "explore"
                    );
                  }}
                  onOpenDetails={(key) => {
                    const item = results.find((r) => itemKey(r) === key);
                    if (!item) return;

                    const idx = results.findIndex((r) => itemKey(r) === key);

                    // UX rule: events navigate directly from map rail in v1
                    if (item.kind === "activity" && item.href) {
                      track(
                        "explore_rail_open",
                        {
                          kind: item.kind,
                          action: "navigate",
                          position_bucket: bucketPosition(idx),
                          distance_km_bucket: bucketDistanceKm((item as any)._distanceKm),
                        },
                        "explore"
                      );

                      navigate(item.href);
                      return;
                    }

                    track(
                      "explore_rail_open",
                      {
                        kind: item.kind,
                        action: "preview",
                        position_bucket: bucketPosition(idx),
                        distance_km_bucket: bucketDistanceKm((item as any)._distanceKm),
                      },
                      "explore"
                    );

                    setSelectedKey(key);
                    setPreviewOpen(true);
                  }}
                />

              </div>
            </div>
          </>
        )}

        {/* LIST VIEW — hero + paths scroll with content; uses h-full instead of absolute inset-0 for iOS Safari compatibility */}
        {viewMode === "list" && (
          <div ref={listRef} className="h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}>
            {/* Micro-hero + Paths — inside scroll container so they scroll naturally */}
            <ExploreHero title={heroTitle.title} subtitle={heroTitle.subtitle} compact />
            <div className="px-4 pb-3">
              <ExplorePaths activePathKey={deriveActivePathKey(mode, exploreParams.tags)} />
            </div>
            {isLoading ? (
              <div className="p-4 space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="aspect-[16/9] w-full rounded-none" />
                    <div className="px-4 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : results.length === 0 ? (
              /* Poetiska tomlägen — Farmly-ton */
              <ExploreEmptyState
                variant={
                  favActive ? "favorites"
                  : activeSearchQuery ? "search"
                  : nearMeActive ? "near-me"
                  : activeFilterCount > 0 ? "filtered"
                  : "no-results"
                }
                searchQuery={activeSearchQuery}
                modeLabel={modeLabel}
                onClearSearch={() => setActiveSearchQuery("")}
                onClearNearMe={() => exploreLocation.clear()}
                onClearFilters={clearAllFilters}
                onSelectCity={exploreLocation.selectCity}
                onToggleFav={toggleFav}
              />
            ) : (
              <>
                {/* Fallback notice (places mode only — "do" never falls back) */}
                {isFallback && mode === "places" && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 text-sm text-muted-foreground">
                    <Info className="h-4 w-4 shrink-0" />
                    <span>Inga {modeLabel} just nu — visar gårdar istället.</span>
                  </div>
                )}
                {/* Utflyktsförslag — aggregated place signals */}
                {mode === "places" && <UtflyktSection />}
                {/* Sektionerad rytm: curated sections → "Alla" sist */}
                {/* UX rule: List = Go. Map = Peek. See design-rules/ux-rules/explore.md */}
                <ExploreSectionList sections={sections} onCardClick={handleExploreCardClick} />
                {/* Discrete result count — never headline */}
                <div className="py-4 text-center text-xs text-muted-foreground/60">
                  Visar {results.length}
                </div>
              </>
            )}
          </div>
        )}

        {/* Unified preview sheet */}
        <ExplorePreviewSheet
          item={selectedItem}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          mode={mode}
        />
      </div>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <button onClick={() => setSearchOpen(false)} aria-label="Stäng sök">
              <ArrowLeft className="h-5 w-5 text-foreground" />
            </button>
            <input
              autoFocus
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
              className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground h-11"
            />
            {searchQuery && (
              <Button size="sm" onClick={handleSearchSubmit}>
                Sök
              </Button>
            )}
          </div>
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm px-8 text-center">
            Skriv ett sökord och tryck Enter.
          </div>
        </div>
      )}

      {/* Sticky footer nav */}
      <ExploreFooterNav mode={mode} onModeChange={setMode} />

      {/* Filter & Sort sheets */}
      <ExploreFilterSheet
        open={filterOpen}
        onOpenChange={setFilterOpen}
        mode={mode}
        params={exploreParams}
        favActive={favActive}
        onToggleFav={toggleFav}
        onApply={applyParams}
        locationActive={nearMeActive}
        locationLabel={exploreLocation.label}
        onRequestGeo={async () => {
          const ok = await exploreLocation.requestGeo();
          if (ok) setViewMode("map");
        }}
        onSelectCity={exploreLocation.selectCity}
        onClearLocation={exploreLocation.clear}
        resultCount={areaFilteredResults.length}
        onClearAll={clearAllFilters}
      />
      <ExploreSortSheet
        open={sortOpen}
        onOpenChange={setSortOpen}
        mode={mode}
        currentSort={exploreParams.sort}
        hasGeo={userLocation != null}
        onSelect={(sort) => applyParams({ sort })}
      />
    </div>
  );
};

export default ExploreMobile;
